<script setup lang="ts">
/**
 * A small toolbar that belongs to one object on a canvas — a selected shape,
 * chart or picture: its fill and line colours, its arrange commands. It floats
 * centred above the object (`anchor`), moves below it when there is no room
 * above, and stays `padding` inside `boundary` (the scrolling canvas; the
 * viewport without one). The boundary may also be a region that is no element
 * of its own — one pane of a split window, the part left of an open sidebar:
 * pass its rect, or a function that returns it when the toolbar is placed.
 * It is shown while `open` says so — nothing toggles it
 * by a click — and follows the anchor as it scrolls, moves or resizes; it hides
 * while the anchor is scrolled out of the boundary.
 *
 * Any kit controls go in the default slot (icon buttons, a colour popover, a
 * number field). Its buttons share one tab stop: Tab enters them at the last
 * used one, ArrowLeft / ArrowRight (Home / End) move between the enabled ones.
 * A text field (input, select, textarea, editable text) keeps its own arrow
 * keys, so it is a tab stop of its own outside that set — Tab and Shift+Tab
 * move between the fields and the buttons, and the toolbar stays traversable
 * whatever order they come in. Keys inside a popover opened from it stay there.
 *
 * Non-modal and raised (the popover surface); it renders in the top layer, so
 * a clipping or transformed canvas does not cut it off.
 */
import { autoUpdate } from '@floating-ui/vue';
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import {
  isContextToolbarBoundaryElement,
  nextRovingIndex,
  placeContextToolbar,
  resolveContextToolbarBoundary,
  type NxContextToolbarBoundary,
  type NxContextToolbarPlacement,
} from './placement';

const props = withDefaults(
  defineProps<{
    /** Shown while true. */
    open: boolean;
    /** The object the toolbar belongs to. */
    anchor: HTMLElement | null;
    /**
     * The box it stays inside. An element (the scrolling canvas) is measured;
     * a rect `{ top, left, right, bottom }` in viewport pixels (a `DOMRect`
     * fits) is taken as it is; a function returning such a rect (or `null`
     * for the viewport) is called on every placement — on scroll, resize and
     * `update()`. Default: the viewport.
     */
    boundary?: NxContextToolbarBoundary;
    /** Accessible name of the toolbar. */
    label?: string;
    /** Gap in px between object and toolbar. Default 8. */
    offset?: number;
    /** Min px kept from the boundary's left and right edge. Default 8. */
    padding?: number;
  }>(),
  { boundary: null, label: undefined, offset: 8, padding: 8 }
);

const root = ref<HTMLElement | null>(null);
const placement = ref<NxContextToolbarPlacement | null>(null);
/** Index of the control that holds the tab stop. */
const current = ref(0);

function viewport() {
  return { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight };
}

/** Measures anchor, boundary and toolbar and places the toolbar. */
function update(): void {
  const el = root.value;
  const anchor = props.anchor;
  if (!el || !anchor) return;
  placement.value = placeContextToolbar(
    anchor.getBoundingClientRect(),
    { width: el.offsetWidth, height: el.offsetHeight },
    resolveContextToolbarBoundary(props.boundary, viewport),
    { offset: props.offset, padding: props.padding }
  );
}

/**
 * A popup or composite inside the toolbar (a popover's panel, a menu, a radio
 * group) keeps its own keys and tab order; the toolbar only roves over
 * controls that sit directly in it.
 */
const NESTED =
  '[popover], [role="dialog"], [role="menu"], [role="listbox"], [role="radiogroup"], [role="grid"], [role="tree"]';

function isDirect(el: Element): boolean {
  return el.closest(NESTED) === root.value;
}

/**
 * A control that needs the arrow keys itself (caret, options, a slider). It
 * keeps its own tab stop instead of joining the roving set, so it never traps
 * the keyboard (WAI-ARIA toolbar pattern).
 */
const FIELD = [
  'textarea',
  'select',
  '[contenteditable]:not([contenteditable="false"])',
  'input:not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="image"]):not([type="checkbox"]):not([type="radio"])',
].join(', ');

/** The toolbar's own enabled roving controls — not its fields, not those of a popup it opened. */
function items(): HTMLElement[] {
  const el = root.value;
  if (!el) return [];
  const focusable = el.querySelectorAll<HTMLElement>(
    'button, input, select, textarea, a[href], [tabindex]'
  );
  return [...focusable].filter(
    (item) =>
      isDirect(item) &&
      !item.matches(FIELD) &&
      !(item as HTMLButtonElement).disabled &&
      // A control's own helper kept out of the Tab order stays out of it.
      (item.hasAttribute('data-nx-roving') || item.getAttribute('tabindex') !== '-1')
  );
}

/** One tab stop: the current control keeps tabindex 0, the others -1. */
function syncTabStops(): void {
  const list = items();
  if (!list.length) return;
  if (current.value >= list.length) current.value = list.length - 1;
  list.forEach((item, index) => {
    item.setAttribute('data-nx-roving', '');
    item.tabIndex = index === current.value ? 0 : -1;
  });
}

function onFocusin(event: FocusEvent): void {
  const index = items().indexOf(event.target as HTMLElement);
  if (index < 0) return;
  current.value = index;
  syncTabStops();
}

function isOwnField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (!isDirect(target)) return true;
  return !!target.closest(FIELD);
}

function onKeydown(event: KeyboardEvent): void {
  if (isOwnField(event.target)) return;
  const list = items();
  const from = Math.max(0, list.indexOf(event.target as HTMLElement));
  const next = nextRovingIndex(from, list.length, event.key);
  if (next === null) return;
  event.preventDefault();
  current.value = next;
  syncTabStops();
  list[next]?.focus();
}

let stopAutoUpdate: (() => void) | null = null;
let observer: MutationObserver | null = null;

function stop(): void {
  stopAutoUpdate?.();
  stopAutoUpdate = null;
  observer?.disconnect();
  observer = null;
}

/** An element boundary restarts the tracking, as before; a rect or function only re-places. */
const boundaryElement = () =>
  isContextToolbarBoundaryElement(props.boundary) ? props.boundary : null;

watch(
  [() => props.open, () => props.anchor, boundaryElement, root],
  async ([open, anchor, , el]) => {
    stop();
    placement.value = null;
    if (!open || !anchor || !el) return;
    await nextTick();
    if ('showPopover' in el) {
      try {
        el.showPopover();
      } catch {
        /* already shown */
      }
    }
    // Scroll, resize and (where the browser has IntersectionObserver) a moved anchor.
    stopAutoUpdate = autoUpdate(anchor, el, update);
    update();
    syncTabStops();
    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(syncTabStops);
      observer.observe(el, { childList: true, subtree: true, attributeFilter: ['disabled'] });
    }
  },
  { immediate: true, flush: 'post' }
);

// A new rect or function: place again, without restarting the tracking.
watch(
  () => props.boundary,
  (boundary) => {
    if (!isContextToolbarBoundaryElement(boundary) && placement.value) update();
  },
  { flush: 'post' }
);

onBeforeUnmount(stop);

defineExpose({
  /** Re-place now — for a host that moves the anchor without layout changes. */
  update,
});
</script>

<template>
  <div
    v-if="props.open"
    ref="root"
    class="nx-context-toolbar"
    :class="{ 'is-placed': !!placement, 'is-hidden': placement && !placement.visible }"
    :data-side="placement?.side"
    :style="placement ? { top: `${placement.top}px`, left: `${placement.left}px` } : undefined"
    popover="manual"
    role="toolbar"
    :aria-label="props.label"
    @focusin="onFocusin"
    @keydown="onKeydown"
  >
    <slot />
  </div>
</template>

<style scoped>
.nx-context-toolbar {
  position: fixed;
  inset: auto;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  width: max-content;
  margin: 0;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  overflow: visible;
  border: var(--nxt-hairline) solid var(--nxt-popover-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-popover-bg);
  box-shadow: var(--nxt-popover-shadow);
  color: var(--nxt-popover-fg);
  font-size: var(--nxt-font-size-xs);
  white-space: nowrap;
  /* A host canvas may be click-through; the toolbar never is. */
  pointer-events: auto;
  /* Measurable but unseen until the first placement. */
  opacity: 0;
}
.nx-context-toolbar.is-placed {
  opacity: 1;
}
.nx-context-toolbar.is-hidden {
  visibility: hidden;
}
</style>
